import { useContext, useEffect, useState } from "react";
import { Button, Typography, Card, Form, message } from "antd";
import { PlayCircleOutlined } from "@ant-design/icons";
import TutorialsPlayModal from "./TutorialsPlayModal";
import Logo from "../assets/Logo.svg";
import AddChannelForm from "./AddChannelForm";
import { useNavigate } from "react-router-dom";
import CopyablePlatformLinks from "./CopyablePlatformLinks";
import { PLATFORM_LINKS } from "../assets/data";
import ChannelList from "./ChannelList";
import { addChannel, fetchChannels, handleChannelOperations, removeChannel, updateSocialUsernames } from "../services/channel";
import SocialUsernameForm from "./SocialUsernameForm";
import { AuthContext } from "../context/authContext";

const Onboarding = () => {
  const { userData, setUserData } = useContext(AuthContext);
  const [step, setStep] = useState("discord");
  const [discordChannels, setDiscordChannels] = useState([]);
  const [telegramChannels, setTelegramChannels] = useState([]);
  const [channelId, setChannelId] = useState("");
  const [channelName, setChannelName] = useState("");
  const [sport, setSport] = useState("");
  const [isTutorialModalVisible, setIsTutorialModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [channelLoading, setChannelLoading] = useState(false);
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const state = {
    step,
    channelId,
    channelName,
    sport,
    discordChannels,
    telegramChannels
  };

  const setState = {
    setChannelId,
    setChannelName,
    setSport,
    setDiscordChannels,
    setTelegramChannels,
    setLoading
  };

  useEffect(() => {
    const loadChannels = async () => {
      try {
        await fetchChannels({
          setDiscordChannels,
          setTelegramChannels,
          setChannelLoading
        });
      } catch (error) {
        console.error("Failed to load channels:", error);
      }
    };
    
    loadChannels();
  }, []);

  const handleFinish = async () => {
    setLoading(true);
    try {
      await handleChannelOperations({
        state,
        setState,
        isOnboarding: true,
        onSuccessCallback: () => message.success("Channel settings updated")
      });
    
      
    const values = await form.getFieldsValue(['discordUsername', 'telegramUsername']);
    
    // Create payload with only non-empty values
    const payload = {};

    if (values.discordUsername || values.discordUsername === "") {
      payload.discordUsername = values.discordUsername;
    }
    if (values.telegramUsername || values.telegramUsername === "") {
      payload.telegramUsername = values.telegramUsername;
    }
    
    if (Object.keys(payload).length > 0) {
      const response = await updateSocialUsernames(userData?.userId, payload);
    
      const storedUserData = localStorage.getItem('userData');
      if (storedUserData) {
        const parsedUserData = JSON.parse(storedUserData);
        const updatedUserData = {
          ...parsedUserData,
          discordUsername:
            response.user.hasOwnProperty("discordUsername")
              ? response.user.discordUsername
              : parsedUserData.discordUsername,
          telegramUsername:
            response.user.hasOwnProperty("telegramUsername")
              ? response.user.telegramUsername
              : parsedUserData.telegramUsername
        };
        
        localStorage.setItem('userData', JSON.stringify(updatedUserData));
        setUserData(updatedUserData);
      }
    }
  
    message.success("All settings updated successfully");
    if(step === "discord" && values.discordUsername === "") {
      message.warning({content:"Please enter your Discord username to receive bot messages.",duration: 5});
    }else if(step === "telegram" && values.telegramUsername === "") {
      message.warning({content:"Please enter your Telegram username to receive bot messages.",duration: 5});
    }    
    navigate('/chat');
  } catch (error) {
    console.error("Error saving settings:", error);
    message.error(error.message || "Failed to save settings");
  } finally {
    setLoading(false);
  }
  };

  const updateStep = () => {
    const values = form.getFieldsValue(['discordUsername', 'telegramUsername']);
    if(values.discordUsername === ""){
      message.warning({content:"Please enter your Discord username to receive bot messages.",duration: 5});
    }
    setStep("telegram");
  }  

  return (
    <div className="flex justify-center bg-gray-100 p-3 min-h-screen overflow-y-auto items-center">

      <Card className="w-full max-w-[500px] border shadow-md rounded-lg bg-white h-full">

        <div className="flex justify-between items-center mb-2">
          <img src={Logo} alt="Logo" className="h-8" />
          <Button
            icon={<PlayCircleOutlined style={{ color: "black" }} />}
            type="text"
            style={{ backgroundColor: "#F2F4F7", color: "black" }}
            onClick={() => setIsTutorialModalVisible(true)}
          >
            Tutorial
          </Button>
        </div>

        <Typography.Title level={4} className="text-left font-[600]">
          {step === "discord" ? "Add Discord Channels" : "Add Telegram Channels"}
        </Typography.Title>

        <SocialUsernameForm
          step={step}
          form={form}
          currentDiscord={userData?.discordUsername || ''}
          currentTelegram={userData?.telegramUsername || ''}
        />

        <div className="mb-3 border-t border-dashed border-gray-400" />

      {/* <Typography.Title level={5} className=" text-left">
        Channels
      </Typography.Title> */}
        <div>
          <AddChannelForm 
            step={step} 
            channelId={channelId} 
            setChannelId={setChannelId} 
            channelName={channelName} 
            setChannelName={setChannelName} 
            sport={sport} 
            setSport={setSport}
            addChannel={() => addChannel(state, setState)}
          />
        </div>
        <Typography.Title level={5} className="mt-6 text-left">
          Added Channels
        </Typography.Title>
        <ChannelList
          channels={step === "discord" ? discordChannels : telegramChannels}
          step={step}
          removeChannel={(index) => removeChannel(state, setState, index)}
          loading={channelLoading}
        />

        <div className="mt-6 mb-3 border-t border-dashed border-gray-400" />

        <div>
          <CopyablePlatformLinks
            platform={step}
            link={PLATFORM_LINKS[step]}
            label={step === "discord" ? "Discord Bot Link" : "Telegram Bot Link"}
          />
        </div>

        <div className="flex justify-between mt-6">
          <Button disabled={loading} onClick={() => {step==="discord"? updateStep(): setStep("discord")}}>
            {step === "discord" ? "Skip" : "Back"}
          </Button>
          <Button
            type="primary"
            className="bg-[#E61F26] text-white hover:bg-[#C91A20]"
            onClick={() => {
              if (step === "discord") {
                updateStep();
              } else {
                handleFinish();
              }
            }}
            loading={loading}
          >
            {step === "discord" ? "Continue" : "Finish"}
          </Button>
        </div>
      </Card>
      {isTutorialModalVisible && (
        <TutorialsPlayModal
          onClose={() => setIsTutorialModalVisible(false)}
          discord={step === "discord"}
        />
      )}
    </div>
  );
};

export default Onboarding;